// The person's pointer, for companions that play with it ("Plays with the pointer", the catalog's
// `pointer`) and to keep them out of its way. The pointer is the person's tool, so play is calm,
// short and never in the way:
//
// - Only where it is, as the host's input reports it (runtime/README.md: input): never what is
//   under it, never which keys. A pointer that can't hover (a touch screen: there is none between
//   taps) or that left the screen (another monitor, out of the browser window) is no one's to play with.
// - It invites play while it idles or wanders slowly, or comes close to a companion and lingers.
// - The person is working with it while they type, click, scroll or drag, and for a moment after
//   it moved with purpose (fast and straight, going somewhere): then every play stops at once, and
//   none starts. A companion never goes where the pointer is (only beside it, keeping clear, so the
//   pointer never ends up over it), so it never takes a click meant for what's under the pointer.
//
// The runtime feeds it every input signal (pointerInput); what it keeps is a few numbers and the
// last two seconds of where the pointer went, so it costs nothing while nobody plays. Abilities use
// the rest: whether to play (calls), the pointer (target), going to it (chase, spotBeside), keeping
// an eye on it (eyesOn), and the shared way to play with it (play) that a design can replace with
// its own ("pointer" in its "moves": an ability with `pointer: { play(g, { shown }), reach, again }`).

import { clockNow } from './clock.js';
import { env, clamp, rand, lerp, tick, wait, animate, isCurrent, Cancelled } from './core.js';
import { cast, heightOf, fits, spotPoint, setShape, pulse } from './companion.js';
import { surfaceBelow, planRoute, visibleOn, restsOnFloor, FLOOR } from './world.js';
import { travelTo, arrive, fallDown, baseOf } from './travel.js';
import { can, walkStyle } from './catalog.js';
import { ability } from './abilities.js';
import { activityOf } from './activity.js';

const HISTORY = 2000;            // ms of where the pointer went that it keeps
const PURPOSE_MS = 300;          // over this long, a move with purpose...
const PURPOSE_WAY = 150;         // ...goes at least this far (logical pixels, net)...
const STRAIGHT = 0.7;            // ...and about straight (net way / way travelled)
const PURPOSE_AFTER = 1200;      // ms it still counts as working after a move with purpose (the click that follows)
const BUSY_AFTER = 3500;         // ms after a key, a click, a scroll or a drag
const SLOW = 220;                // logical pixels a second: slower than this, the pointer wanders
const REST = 1500;               // ms without moving: the pointer rests
const FORGET = 60000;            // ms of rest after which a resting pointer no longer calls (the person left)
const LINGER = 1400;             // ms close to a companion: it lingers there
const NEAR = 26;                 // how close is close, its pixels from its body
const AGAIN = [90, 200];         // seconds between plays at normal activity (the shared play; an own play says)

// ---------- where the pointer is ----------
const pt = { x: 0, y: 0, here: false, hover: false, pressed: false, seen: false,
  since: -Infinity, movedAt: -Infinity, busyAt: -Infinity, hurryAt: -Infinity };
const trail = [];                // { t, x, y }, oldest first: the last HISTORY ms, and where it was before that

// Every input signal the runtime hears: `move` (with `pointerType` and `pressed` when the host
// knows them), `out` (it left this screen), `down`, `up`, `cancel`, `key`, `activity` (a click,
// a scroll), `idle`.
export function pointerInput(e, now = clockNow()) {
  switch (e.type) {
    case 'move':
      if (!pt.here) { pt.here = true; pt.since = now; trail.length = 0; }
      Object.assign(pt, { x: e.x, y: e.y, movedAt: now, seen: true, hover: e.pointerType !== 'touch', pressed: !!e.pressed });
      if (pt.pressed) pt.busyAt = now;                     // dragging something
      trail.push({ t: now, x: e.x, y: e.y });
      while (trail.length > 2 && trail[1].t < now - HISTORY) trail.shift();
      if (withPurpose(now)) pt.hurryAt = now;
      break;
    case 'out': pt.here = false; pt.pressed = false; trail.length = 0; break;
    case 'down': pt.pressed = true; pt.busyAt = now; break;
    case 'up': case 'cancel': pt.pressed = false; pt.busyAt = now; break;
    case 'key': case 'activity': pt.busyAt = now; break;
    default: break;
  }
}
// Forgets it all (a runtime going away, or the gallery starting a recording).
export function resetPointer() {
  Object.assign(pt, { here: false, hover: false, pressed: false, seen: false, since: -Infinity, movedAt: -Infinity, busyAt: -Infinity, hurryAt: -Infinity });
  trail.length = 0;
}

const lp = px => px / (env.unit || 1);                    // canvas pixels to logical ones
// Where it was at time t (the last place it was seen by then).
function at(t) {
  for (let i = trail.length - 1; i >= 0; i--) if (trail[i].t <= t) return trail[i];
  return trail[0];
}
// The way travelled from time t to now (canvas pixels).
function way(t) {
  let d = 0;
  for (let i = trail.length - 1; i > 0 && trail[i].t > t; i--) d += Math.hypot(trail[i].x - trail[i - 1].x, trail[i].y - trail[i - 1].y);
  return d;
}
// Going somewhere: far and about straight over the last PURPOSE_MS.
function withPurpose(now) {
  const from = at(now - PURPOSE_MS), to = trail[trail.length - 1];
  if (!from || !to) return false;
  const net = Math.hypot(to.x - from.x, to.y - from.y);
  return lp(net) >= PURPOSE_WAY && net >= STRAIGHT * way(now - PURPOSE_MS);
}

// The pointer on this screen ({ x, y, hover }), or null. For the eyes that follow it (the runtime).
export const pointerAt = () => (pt.here ? { x: pt.x, y: pt.y, hover: pt.hover } : null);
// How far it went over the last `ms` (canvas pixels): a lot of it wakes sleepers (the runtime).
export const travelled = (ms, now = clockNow()) => (pt.here ? way(now - ms) : 0);
// The person is working with it: typing, clicking, scrolling or dragging just now, or it just
// moved with purpose.
export const working = (now = clockNow()) => pt.pressed || now - pt.busyAt < BUSY_AFTER || now - pt.hurryAt < PURPOSE_AFTER;
// It could be played with now: a pointer that hovers, here, and nobody working with it.
export const calm = (now = clockNow()) => pt.here && pt.hover && !working(now);
// How long it has rested where it is (0 while it moves or isn't here).
export const restingFor = (now = clockNow()) => (pt.here ? now - pt.movedAt : 0);
// Wandering: it moved in the last moment, slowly.
const wanders = now => now - pt.movedAt < 500 && lp(way(now - 1000)) < SLOW;

// How far the point is from its body (its box as last drawn, else where its body stands), canvas
// pixels; 0 inside it.
export function distanceTo(me, p = pt) {
  const s = me.s, b = me.bbox || { l: me.x - 7 * s, r: me.x + 7 * s, t: me.y - heightOf(me), b: me.y };
  return Math.hypot(Math.max(b.l - p.x, 0, p.x - b.r), Math.max(b.t - p.y, 0, p.y - b.b));
}
// The room it keeps around the pointer (canvas pixels): the hit regions' own margins and a little more.
export const clearOf = me => 3 * me.s + 6 * (env.unit || 1);
// Close to it (but not on it: the person may be about to pick it up) the whole last `ms`.
function lingers(me, ms, now) {
  if (now - pt.since < ms) return false;
  const near = p => { const d = distanceTo(me, p); return d > clearOf(me) && d < NEAR * me.s; };
  if (!near(pt)) return false;
  for (let i = trail.length - 1; i >= 0; i--) {
    if (!near(trail[i])) return false;
    if (trail[i].t <= now - ms) break;                     // where it was when the time began
  }
  return true;
}

// ---------- whether to play ----------
// A design's own way to play with the pointer ("pointer" in its "moves"), or null for the shared one.
const ownPlay = me => ability(me.design.moves?.pointer)?.pointer || null;
// Whether the pointer invites it to play now: it plays with it (its switch), it isn't waiting after
// the last time, and the pointer is calm (and not left alone for a long while: nobody is there)
// and lingers close to it. One whose own play reaches the
// whole screen ("reach": "screen": Neko, Cursor) also comes when the pointer rests or wanders
// anywhere it can see. Cheap enough to ask every frame while it rests (acts.js: dwell).
export function calls(me, now = clockNow()) {
  const rest = restingFor(now);
  if (!calm(now) || rest > FORGET || now < (me.pointerAgain || 0) || !can(me, 'pointer')) return false;
  if (lingers(me, LINGER, now)) return true;
  if (ownPlay(me)?.reach !== 'screen' || distanceTo(me) < clearOf(me)) return false;
  return rest > REST || wanders(now);
}

// It plays with the pointer: its own way, or the shared one. `shown`: the menu's Show me asked for
// it (runtime/showme.js), so it plays with wherever the pointer is, even while the person works
// (they asked), and with a pretend one when there is none on its screen. Afterwards it waits a
// while before it plays again (its activity level makes that longer or shorter).
export async function play(g, { shown = false } = {}) {
  const me = g.me, own = ownPlay(me);
  try {
    await (own ? own.play(g, { shown }) : playAlong(g, { shown }));
  } finally {
    const [lo, hi] = own?.again || AGAIN;
    me.pointerAgain = clockNow() + rand(lo, hi) * 1000 * activityOf(me).rest;
    me.hurry = false;
  }
}

// ---------- going to it ----------
// The pointer as it plays with it: where it is, kept on the screen; for a Show me with none here,
// where it was last seen, or a pretend one up beside it.
export function target(me, shown = false) {
  let p = pt.here && pt.hover ? pt : shown ? (pt.seen ? pt : { x: me.x + (me.face || 1) * 30 * me.s, y: me.y - 24 * me.s }) : null;
  if (!p) return null;
  return { x: clamp(p.x, 0, env.W), y: clamp(p.y, 0, env.H) };
}

// Half its width at its widest (front or side-on, whichever is wider), canvas pixels.
export const halfWidth = me => (Math.max(me.shapes.normal.bw, me.shapes.side.bw) / 2 + 1) * me.s;

// A spot on the surface under the point p, beside it on the side `from` is (its own x by default):
// right under it when the point is high enough above that surface for its head to stay clear of
// it, else beside it, clear of it (`under`: right under it whatever, for a point that isn't the
// pointer). Where another companion is (or is going), the other side, or a step further out. Null
// when there's no such spot (no room there, it doesn't show, or it's floor it may not stay on).
export function spotBeside(me, p, { from = me.x, under = false } = {}) {
  const s = me.s, below = surfaceBelow(p.x, p.y, undefined, undefined, l => fits(me, l)), l = below.ledge, g = below.g;
  const half = 9 * s, clear = clearOf(me), low = !under && p.y > g.y - heightOf(me) - clear;
  if (g.x2 - g.x1 < 2 * half) return null;
  const side = Math.sign(from - p.x) || 1, beside = halfWidth(me) + clear, step = 2 * halfWidth(me);
  const tries = low ? [side * beside, -side * beside, side * (beside + step), -side * (beside + step)] : [0, side * step, -side * step];
  for (const off of tries) {
    const x = clamp(p.x + off, g.x1 + half, g.x2 - half);
    if (low && Math.abs(x - p.x) < beside) continue;     // the surface ends there: it would be under the pointer
    if (!visibleOn(l, x, g.y, s) || (l === FLOOR && !restsOnFloor(x - 9 * s, x + 9 * s)) || taken(me, x, g.y)) continue;
    return { ledge: l, dx: x - g.x1, rest: 'stand' };
  }
  return null;
}
// Another companion stands at x on that height, or is going there.
const taken = (me, x, y) => cast.some(o => o !== me && o.alpha > 0.5 && [o, o.dest && spotPoint(o, o.dest)]
  .some(q => q && Math.abs(q.y - y) < 2 * me.s && Math.abs(q.x - x) < 0.8 * (halfWidth(me) + halfWidth(o))));

// A run token for a part of a move that stop() can cut short: g's own (its companion, its tempo),
// but once stop() says so its next frame throws Cancelled, as if a new run had begun. The move
// catches that and goes on with g (chase does: it stops where it is).
export const stoppable = (g, stop) => Object.create(g, { n: { get: () => (stop() ? NaN : g.n) } });

// Cut short on its way (stoppable): it stays where it is, standing on what it was walking along,
// or, in the air (a hop cut short), comes down onto what's below.
async function stopHere(g, ledges) {
  const me = g.me, p = me.pose;
  Object.assign(p, { legs: 'stand', stride: 0 });
  me.bob = 0; me.hurry = false;
  if (me.rotAround === 'center' && me.rot) {                 // a roller settles upright
    const r0 = me.rot % (2 * Math.PI), r1 = Math.round(r0 / (2 * Math.PI)) * 2 * Math.PI;
    await animate(200, g, t => { me.rot = r0 + (r1 - r0) * t; });
  }
  me.rot = 0; me.rotAround = 'feet';
  me.y -= me.jump; me.jump = 0;
  for (const l of ledges) {
    const q = l?.geom();
    if (q && Math.abs(q.y - me.y) <= 2 && me.x >= q.x1 && me.x <= q.x2) { arrive(me, { ledge: l, dx: me.x - q.x1, rest: 'stand' }); return; }
  }
  await fallDown(g);
}

// Off to where the pointer is, in a hurry: along its surface and by small hops (and, `far`, its
// own ways to anywhere), aiming again whenever the pointer moves on, until it's there (true). It
// stops where it is (false) when the person starts working with the pointer (not for a Show me),
// when the pointer goes, or when it can't get there its way. `until`: a time it gives up at.
export async function chase(g, { far = false, shown = false, until = Infinity } = {}) {
  const me = g.me;
  for (let tries = 0; tries < 8 && clockNow() < until; tries++) {
    const p = target(me, shown), spot = p && spotBeside(me, p);
    if (!spot || (!shown && !calm())) return false;
    const goal = spotPoint(me, spot), from = me.anchor ? baseOf(me.anchor) : null;
    if (from?.id === spot.ledge.id && Math.abs(goal.x - me.x) < 3 * me.s) return true;
    const route = from && from.id !== spot.ledge.id ? planRoute(from, spot.ledge, env.S) : [];
    const near = from && route && route.length <= 4;
    if (!near && !far) return false;                         // out of its reach
    if (!near) { await travelTo(spot, g); continue; }        // its own way, far: never cut short in mid-air
    // On the way it looks now and then whether the pointer moved on (a little way along is fine).
    let checked = 0, moved = false;
    const stop = () => {
      if (!shown && !calm()) return true;
      const now = clockNow();
      if (now - checked > 150) {
        checked = now;
        const q = target(me, shown), next = q && spotBeside(me, q);
        moved = !next || next.ledge.id !== spot.ledge.id || Math.abs(next.dx - spot.dx) > 20 * me.s;
      }
      return moved;
    };
    me.hurry = true;
    try {
      await travelTo(spot, stoppable(g, stop), from.id === spot.ledge.id ? 'walk' : undefined);
    } catch (e) {
      if (!(e instanceof Cancelled) || !isCurrent(g)) throw e;
      await stopHere(g, [from, spot.ledge]);
    } finally { me.hurry = false; }
  }
  return false;
}

// Its eyes on the point: the way it is from its head (and, side-on, it faces that way).
export function eyesOn(me, p) {
  const dx = p.x - me.x, dy = p.y - (me.y - heightOf(me) * 0.6);
  me.pose.eyes = Math.abs(dx) > Math.abs(dy) * 0.8 ? (dx < 0 ? 'left' : 'right') : dy < 0 ? 'up' : 'down';
}

// ---------- the shared way to play ----------
// Fliers (it flies, or hovers by itself) follow it in the air; the others go to it along what they
// stand on and pounce up at it.
const flies = me => walkStyle(me.design) === 'fly' || !!me.design.body.bob;

// For a while it plays: it notices the pointer (a little stretch), goes to it in a hurry and jumps
// up at it, again if it moves on, and gives up when the pointer rests a while or the person works
// with it. Then a happy hop. A flier follows it about in the air instead and settles again.
async function playAlong(g, { shown }) {
  const me = g.me, s = me.s, end = clockNow() + (shown ? 9000 : rand(6000, 10000));
  let p = target(me, shown);
  if (!p) return;
  eyesOn(me, p);
  pulse(me, 'stretch');
  await wait(350, g);
  if (flies(me)) { await followInAir(g, { shown, end }); return; }
  let jumps = 0;
  while (clockNow() < end && jumps < 4) {
    if (!shown && !calm()) break;                            // the person is working: it stops at once
    if (!await chase(g, { shown, until: end })) {
      if (!shown) break;
      await wait(300, g); jumps++; continue;                 // (a Show me where it can't get there: it jumps where it is)
    }
    p = target(me, shown);
    if (!p) break;
    await pounce(g, p);
    jumps++;
    // It watches the pointer a moment; one that rests a while, it loses interest in.
    for (const t0 = clockNow(); clockNow() - t0 < 900; ) {
      await tick(g);
      const q = target(me, shown);
      if (q) eyesOn(me, q);
    }
    if (!shown && restingFor() > 2500) break;
  }
  setShape(me, 'normal', 'front');
  me.pose.eyes = 'center';
  await wait(200, g);
  if (!working() || shown) {
    await animate(260, g, t => { me.jump = 4 * 2.2 * s * t * (1 - t); });
    me.jump = 0;
  }
}

// Up at it: it crouches, eyes on it, and jumps straight up, as high as it can while its head stays
// clear of the pointer (a pointer right beside it gets a quick little lunge instead).
async function pounce(g, p) {
  const me = g.me, s = me.s, sh = me.view === 'side' ? me.shapes.side : me.shapes.normal, dir = Math.sign(p.x - me.x) || me.face || 1;
  if (me.design.body.side) setShape(me, 'side', 'side', dir);
  eyesOn(me, p);
  await wait(150, g);
  me.target = { ...me.target, leg: Math.max(0, sh.leg - 1), bh: Math.max(2, sh.bh - 1) };   // down low
  try { await animate(420, g, t => { me.rot = -dir * 0.04 * Math.sin(t * Math.PI * 6); }); } finally { me.rot = 0; }
  me.target = { ...me.target, leg: sh.leg, bh: sh.bh };
  const room = me.y - p.y - heightOf(me) - clearOf(me);
  if (room < 3 * s) {                                        // beside it: a little lunge and back
    const x0 = me.x;
    await animate(240, g, t => { me.x = x0 + dir * 2 * s * Math.sin(Math.PI * t); me.jump = 1.5 * s * Math.sin(Math.PI * t); });
    me.x = x0; me.jump = 0;
    return;
  }
  const h = Math.min(room, 14 * s);
  pulse(me, 'stretch');
  await animate(clamp(h / s * 40, 320, 620), g, t => { me.jump = 4 * h * t * (1 - t); });
  me.jump = 0;
  pulse(me, me.view === 'side' ? 'sideSquash' : 'squash');
  await wait(120, g);
}

// A flier follows the pointer about in the air beside it (never right under or over it), its eyes
// on it, until the time is up or the pointer stops inviting it; then it floats back down onto what
// is below it.
async function followInAir(g, { shown, end }) {
  const me = g.me, s = me.s, p = me.pose, home = me.anchor;
  const from = me.anchor ? baseOf(me.anchor) : null;
  me.anchor = null; me.moving = true; me.dest = home;       // (interrupted, it's back on its spot at once)
  let t0 = clockNow(), last = t0, still = 0;
  try {
    while (clockNow() < end) {
      await tick(g);
      const now = clockNow(), dt = Math.min(0.1, (now - last) / 1000); last = now;
      if (!shown && !calm(now)) break;
      const q = target(me, shown);
      if (!q) break;
      // Beside it, its middle about level with it, on the side it's on.
      const side = Math.sign(me.x - q.x) || 1, h = heightOf(me);
      const goal = { x: clamp(q.x + side * (halfWidth(me) + clearOf(me) + 4 * s), halfWidth(me), env.W - halfWidth(me)),
        y: clamp(q.y + h * 0.5, h + 2 * s, env.H) };
      const dx = goal.x - me.x, dy = goal.y - me.y, d = Math.hypot(dx, dy), v = Math.min(d, 30 * s * dt);
      if (d > 0.5) { me.x += dx / d * v; me.y += dy / d * v; still = 0; } else still += dt;
      if (Math.abs(dx) > 4 * s && me.design.body.side) setShape(me, 'side', 'side', Math.sign(dx));
      else setShape(me, 'normal', 'front');
      me.bob = Math.round(Math.sin((now - t0) / 260) * 0.8 * s);
      eyesOn(me, q);
      if (!shown && still > 2.5) break;                      // the pointer rests: it loses interest
    }
  } finally { me.bob = 0; p.eyes = 'center'; }
  // Down onto what's below it (or back where it was).
  const spot = spotBeside(me, { x: me.x, y: me.y }, { under: true }) || home;
  const goal = spot && spotPoint(me, spot);
  if (goal) {
    const x0 = me.x, y0 = me.y, dist = Math.hypot(goal.x - x0, goal.y - y0);
    setShape(me, 'normal', 'front');
    await animate(clamp(dist / (20 * s), 0.3, 3) * 1000, g, t => { const k = t * t * (3 - 2 * t); me.x = lerp(x0, goal.x, k); me.y = lerp(y0, goal.y, k); });
    arrive(me, spot);
  } else if (from) arrive(me, home);
  else await fallDown(g);
}
